import type { HTMLAttributes } from 'react'
import { CountryFlagCircle } from '@/shared/components/CountryFlagCircle'
import { CountrySearchHighlight } from '@/shared/components/CountrySearchHighlight'

export type PopularDestinationCountryCardVariant = 'preview' | 'grid'

export interface PopularDestinationCountryCardProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'children' | 'onClick'> {
  flag: string
  isoCode?: string
  name: string
  /** Numeric string, e.g. "0.48" — see priceDisplay */
  pricePerGb: string
  /** perGb: USD {price}/GB (Shop). minBundleUsd: USD {price} total plan floor (Home API). */
  priceDisplay?: 'perGb' | 'minBundleUsd'
  /** When false (Home API), show a neutral line instead of a price. */
  available?: boolean
  onClick: () => void
  variant: PopularDestinationCountryCardVariant
  /** Kept for call-site compatibility (stagger was motion-only). */
  gridIndex?: number
  /** When set, highlights the matching prefix in the country name (shop search). */
  highlightQuery?: string
}

export function PopularDestinationCountryCard({
  flag,
  isoCode,
  name,
  pricePerGb,
  priceDisplay = 'perGb',
  available = true,
  onClick,
  variant,
  gridIndex: _gridIndex = 0,
  highlightQuery,
  className = '',
  ...rest
}: PopularDestinationCountryCardProps) {
  const hoverClass =
    variant === 'preview'
      ? 'hover:shadow-lg hover:-translate-y-0.5 hover:scale-[1.02]'
      : 'hover:shadow-lg hover:-translate-y-1 hover:scale-[1.03]'

  return (
    <div
      {...rest}
      onClick={onClick}
      className={[
        'bg-white rounded-xl shadow-md p-6 text-center cursor-pointer',
        'transition duration-200 will-change-transform',
        hoverClass,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="mb-3 flex w-full justify-center">
        <CountryFlagCircle
          isoCode={isoCode}
          emojiFallback={flag}
          size="compact"
          alt={`${name} flag`}
        />
      </div>
      <div className="text-gray-900 font-semibold mb-2 text-sm">
        {highlightQuery ? <CountrySearchHighlight name={name} query={highlightQuery} /> : name}
      </div>
      <div className="text-gray-500 text-xs mb-1">Starting from</div>
      {available ? (
        <div className="text-telgo-red font-bold text-sm">
          {priceDisplay === 'minBundleUsd'
            ? `USD ${pricePerGb}`
            : `USD ${pricePerGb}/GB`}
        </div>
      ) : (
        <div className="text-gray-400 text-sm font-medium">See shop for plans</div>
      )}
    </div>
  )
}
